import React from 'react';
import { ArrowRight, Gamepad2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loginWithDiscord } from '../lib/api';

const Login = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading } = useAuth();

  // Redirecionar se já estiver autenticado (após loading terminar)
  React.useEffect(() => {
    if (!loading && isAuthenticated && user) {
      if (user.status === 'approved') {
        navigate('/feed', { replace: true });
      } else {
        navigate('/pendente', { replace: true });
      }
    }
  }, [isAuthenticated, user, loading, navigate]);

  const handleDiscordLogin = async () => {
    try {
      // Login direto com Discord, sem passar pela página de termos
      await loginWithDiscord();
    } catch (error) {
      console.error('Erro ao fazer login com Discord:', error);
    }
  };

  const features = [
    { icon: "🏆", text: "Rankings Globais", color: "text-yellow-400" },
    { icon: "🎬", text: "Feed de Filmes e Séries", color: "text-cyan-400" },
    { icon: "🛡️", text: "Perfil Personalizado", color: "text-fuchsia-400" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-purple-950 text-white flex items-center justify-center p-4 relative overflow-hidden">
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-fuchsia-600/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Scanlines Effect */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%)] bg-[length:100%_4px] pointer-events-none opacity-10"></div>

      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center relative z-10">
        
        {/* Left Side - Branding */}
        <div className="hidden lg:block space-y-8">
          <div className="flex items-center gap-3 mb-12">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-cyan-400 to-fuchsia-600 rounded-full blur-lg opacity-75"></div>
              <img src="/oasis-logo.ico" alt="Oasis" className="relative w-12 h-12 rounded-full" />
            </div>
            <div>
              <h1 className="text-4xl font-black tracking-widest italic">OASIS</h1>
              <p className="text-xs text-gray-500 tracking-wider">GAMING COMMUNITY</p>
            </div>
          </div>

          <div>
            <h2 className="text-5xl font-black mb-4 leading-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400">
                Entre no
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-fuchsia-600">
                Próximo Nível
              </span>
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              Junte-se à comunidade OASIS. Inspirada no universo do Jogador Número 1, aqui você encontra rankings épicos, recomendações de filmes e séries, e a chance de se conectar com gamers do mundo todo. O futuro do entretenimento está aqui.
            </p>

            <div className="space-y-4">
              {features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-4 group">
                  <div className="p-3 bg-white/5 border border-white/10 rounded-sm group-hover:border-cyan-500/50 transition-all">
                    <span className={`text-2xl ${feature.color}`}>{feature.icon}</span>
                  </div>
                  <span className="font-bold text-gray-300 group-hover:text-white transition-colors">
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span>250+ membros ativos</span>
            </div>
            <span>•</span>
            <span>50K+ horas em call</span>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full">
          <div className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-sm p-8 shadow-2xl">
            
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-400 to-fuchsia-600 rounded-full blur opacity-75"></div>
                <img src="/oasis-logo.ico" alt="Oasis" className="relative w-8 h-8 rounded-full" />
              </div>
              <span className="text-2xl font-black tracking-widest italic">OASIS</span>
            </div>

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2">Bem-vindo ao OASIS</h2>
              <p className="text-gray-400">Entre com sua conta Discord para acessar a plataforma</p>
            </div>

            {/* Discord OAuth Login */}
            <button
              onClick={handleDiscordLogin}
              className="w-full flex items-center justify-center gap-3 py-4 bg-[#5865F2] hover:bg-[#4752C4] rounded-sm font-bold text-sm transition-all group mb-6 shadow-lg hover:shadow-[0_0_20px_rgba(88,101,242,0.5)]"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              <span>Entrar com Discord</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>

            {/* Informações adicionais */}
            <div className="bg-white/5 border border-white/10 rounded-sm p-4 mb-6">
              <h3 className="font-bold text-sm mb-2 text-cyan-400">Por que entrar com Discord?</h3>
              <p className="text-sm text-gray-400">
                O OASIS utiliza o Discord como plataforma principal de comunicação. 
                Ao entrar com sua conta Discord, você terá acesso a todos os recursos 
                da comunidade e poderá participar dos eventos e discussões.
              </p>
            </div>

            {/* Citação do Ready Player One */}
            <div className="border-l-2 border-fuchsia-500 pl-4 py-1">
              <p className="text-sm italic text-gray-300">
                "Um lugar onde as regras do mundo real não se aplicam."
              </p>
              <p className="text-xs text-gray-500 mt-1">— Ready Player One</p>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>
              Ao entrar, você concorda com nossos{' '}
              <a href="#" className="text-cyan-400 hover:text-cyan-300 font-bold transition-colors">
                Termos de Serviço
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;